import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Undo2 } from "lucide-react";

interface RefundInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paidAmount: string | number | null | undefined;
  isPending: boolean;
  onConfirm: (args: { amount: string; reason: string }) => void;
}

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${Math.abs(num).toFixed(2)}`;
}

export default function RefundInvoiceDialog({
  open,
  onOpenChange,
  paidAmount,
  isPending,
  onConfirm,
}: RefundInvoiceDialogProps) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setAmount(String(paidAmount ?? ""));
      setReason("");
    }
  }, [open, paidAmount]);

  const numericAmount = Number(amount);
  const canSubmit = reason.trim().length > 0 && numericAmount > 0 && !isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refund Invoice</AlertDialogTitle>
          <AlertDialogDescription>
            Mark this invoice as refunded in Staio. This is an accounting record only — it does not move money in Stripe or anywhere else. Issue the actual refund separately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="refund-amount">Refund Amount ($)</Label>
            <Input
              id="refund-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="input-refund-amount"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Amount paid: {formatMoney(paidAmount)}
            </p>
          </div>
          <div>
            <Label htmlFor="refund-reason">Reason</Label>
            <Textarea
              id="refund-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this invoice being refunded?"
              data-testid="input-refund-reason"
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="button-cancel-refund">Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => {
              if (canSubmit) onConfirm({ amount, reason: reason.trim() });
            }}
            disabled={!canSubmit}
            data-testid="button-confirm-refund"
          >
            {isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Undo2 className="h-4 w-4 mr-2" />}
            Confirm Refund
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
